import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import LeadForm from "@/components/LeadForm";
import { FadeUp, MaskedLines, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, LuxeButton, ChapterHeading } from "@/components/shared";
import { ArrowRight, ConciergeBell, Home, MapPin, Sparkles } from "lucide-react";

const VILLA_IMG = "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/7-1920w.png";
const HERO_IMG = "https://images.unsplash.com/photo-1582610116397-edb318620f90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHx8dHJvcGljYWwlMjB2aWxsYXxlbnwwfHx8MTc4ODk2MTAzM318MA&ixlib=rb-4.1.0&q=85";

const DESTINATIONS = [
  { name: "Cap Cana", img: VILLA_IMG, to: "/dominican-republic" },
  { name: "South Carolina", img: "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/Screenshot-2024-07-28-at-4.16.55-PM-1920w.webp", to: "/contact" },
  { name: "Nomad City", img: "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/Screenshot-2024-10-08-at-3.24.47-PM-1920w.webp", to: "/contact" },
  { name: "Dominican Republic", img: "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/IMG_5529-5c02de18-1920w.webp", to: "/dominican-republic" },
];

const TOURS = [
  { label: "Private Residence — Interior Tour", src: "https://virtualhome360.pt/app/viewer/index.php?code=f076073b2082f8741a9cd07b789c77a0" },
  { label: "Private Residence — Full Property Tour", src: "https://virtualhome360.pt/app/viewer/index.php?code=f8bf09f5fceaea80e1f864a1b48938bf" },
];

const VirtualTour = ({ label, src, testId }) => (
  <div className="border border-warm-border bg-white p-3 shadow-[0_20px_60px_rgba(0,0,0,0.05)]" data-testid={testId}>
    <div className="flex items-center justify-between border-b border-warm-border px-4 py-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">{label}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">360° Tour</p>
    </div>
    <iframe
      src={src}
      title={label}
      className="h-[380px] w-full border-0 sm:h-[480px] lg:h-[600px]"
      allowFullScreen
      loading="lazy"
    />
  </div>
);

const VacationHomes = () => (
  <>
    <Seo
      title="Vacation Homes | Luxury Villas & Private Retreats — Scherif Real Estate"
      siteName="Scherif Real Estate"
      description="Discover exceptional vacation homes and private retreats curated by Scherif Real Estate — from the beaches of the Dominican Republic to luxury destinations beyond."
    />

    {/* HERO */}
    <section className="relative flex min-h-screen items-end overflow-hidden bg-obsidian" data-testid="vacation-hero">
      <img src={HERO_IMG} alt="Tropical luxury villa at dusk — vacation homes by Scherif Real Estate" className="absolute inset-0 h-full w-full object-cover opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/20" aria-hidden="true" />
      <div className="container-luxe relative z-10 pb-28 pt-40">
        <Eyebrow dark className="mb-8">Vacation Homes</Eyebrow>
        <h1 className="max-w-4xl font-serif text-[12vw] font-light leading-[1.04] tracking-tight text-ivory sm:text-7xl" data-testid="vacation-headline">
          <MaskedLines delay={0.3} lines={["Escape Somewhere", "Extraordinary."]} />
        </h1>
        <FadeUp delay={0.9}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/85 sm:text-lg">
            Discover exceptional vacation homes and private retreats curated by Scherif Real Estate.
          </p>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/60">
            From the beaches of the Dominican Republic to luxury destinations beyond, experience properties designed for unforgettable stays.
          </p>
          <div className="mt-10">
            <LuxeButton href="#exclusive" variant="light" testId="vacation-explore-btn">Explore Vacation Homes</LuxeButton>
          </div>
        </FadeUp>
      </div>
    </section>

    <Marquee dark items={["Cap Cana", "Punta Cana", "Private Retreats", "Dominican Republic", "Luxury Villas", "Soby & Associates"]} />

    {/* EXCLUSIVE LISTING */}
    <section id="exclusive" className="container-luxe scroll-mt-28 py-24 sm:py-36" data-testid="exclusive-listing">
      <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
        <FadeUp className="lg:col-span-7">
          <div className="img-zoom">
            <img src={VILLA_IMG} alt="Luxury Dominican Republic villa with private pool, walk to beach" className="aspect-[16/10] w-full object-cover" />
          </div>
        </FadeUp>
        <div className="lg:col-span-5">
          <ChapterHeading eyebrow="Exclusive Listing" title="Luxury Dominican Republic Villa" />
          <FadeUp delay={0.2}>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Private Pool", "Walk to Beach", "12 Beds"].map((h) => (
                <span key={h} className="border border-champagne/50 bg-gold-tint px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal">
                  {h}
                </span>
              ))}
            </div>
            <p className="mt-8 text-base leading-relaxed text-slate-muted">
              Experience tropical luxury in this spacious Dominican Republic villa. Perfect for large groups or families, this property features a
              private pool, modern amenities, and easy walking access to the beach. Enjoy a relaxing getaway surrounded by sun, sand, and style.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <LuxeButton to="/dominican-republic" variant="dark" testId="villa-more-details-btn">More Details</LuxeButton>
              <LuxeButton href="#inquiry" variant="outline-dark" testId="villa-inquire-btn">Inquire About Availability</LuxeButton>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* VIRTUAL TOURS */}
    <section className="bg-obsidian py-24 sm:py-36" data-testid="virtual-tours">
      <div className="container-luxe">
        <div className="mb-16 max-w-2xl">
          <ChapterHeading number="01" eyebrow="Virtual Tours" title="Step Inside" dark />
          <FadeUp delay={0.15}>
            <p className="mt-6 text-base leading-relaxed text-ivory/70">
              Explore select properties from anywhere in the world with our immersive virtual tours.
            </p>
          </FadeUp>
        </div>
        <Stagger className="space-y-14">
          {TOURS.map((t, i) => (
            <StaggerItem key={t.src}>
              <VirtualTour label={t.label} src={t.src} testId={`virtual-tour-${i + 1}`} />
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* DESTINATIONS */}
    <section className="container-luxe py-24 sm:py-36" data-testid="destinations">
      <FadeUp className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow className="mb-4">The Map</Eyebrow>
          <h2 className="font-serif text-4xl font-light tracking-tight text-charcoal sm:text-5xl">Explore Our Destinations</h2>
        </div>
      </FadeUp>
      <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {DESTINATIONS.map((d, i) => (
          <StaggerItem key={d.name} className={i % 2 === 1 ? "lg:mt-12" : ""}>
            <a href={d.to} className="img-zoom group relative block" data-testid={`destination-${d.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <img src={d.img} alt={`${d.name} — vacation destination`} className="aspect-[3/4] w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/85 via-obsidian/10 to-transparent transition-opacity duration-700 group-hover:opacity-90" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 w-full p-6">
                <p className="font-serif text-2xl font-light text-ivory">{d.name}</p>
                <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">
                  Explore <ArrowRight size={12} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-2" aria-hidden="true" />
                </p>
              </div>
            </a>
          </StaggerItem>
        ))}
      </Stagger>
    </section>

    {/* EXPERIENCE */}
    <section className="border-y border-warm-border bg-stone-beige py-24 sm:py-32" data-testid="vacation-experience">
      <div className="container-luxe">
        <FadeUp className="mb-16 max-w-3xl">
          <Eyebrow className="mb-4">The Experience</Eyebrow>
          <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-charcoal sm:text-5xl">
            More Than a Stay. It&apos;s an Experience.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-muted">
            Scherif Real Estate provides access to carefully selected vacation properties for families, groups, and travelers looking for memorable
            accommodations.
          </p>
        </FadeUp>
        <Stagger className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Home, title: "Private Retreats" },
            { icon: MapPin, title: "Exceptional Locations" },
            { icon: Sparkles, title: "Luxury Amenities" },
            { icon: ConciergeBell, title: "Personalized Service" },
          ].map(({ icon: Icon, title }, i) => (
            <StaggerItem key={title} className="group border-t border-charcoal/10 pt-8">
              <div className="flex items-center justify-between">
                <Icon size={24} strokeWidth={1} className="text-champagne transition-transform duration-700 group-hover:-translate-y-1" aria-hidden="true" />
                <span className="font-serif text-2xl font-light italic text-charcoal/15">0{i + 1}</span>
              </div>
              <h3 className="mt-5 font-mono text-xs uppercase tracking-[0.3em] text-charcoal">{title}</h3>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* INQUIRY */}
    <section id="inquiry" className="container-luxe scroll-mt-28 py-24 sm:py-36" data-testid="vacation-inquiry">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <ChapterHeading number="02" eyebrow="Begin" title="Planning Your Next Escape?" />
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate-muted">
              Tell us what you&apos;re looking for and our team will help you find the right vacation property.
            </p>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-charcoal">
              Prefer to talk?{" "}
              <a href="tel:+12403724804" className="text-champagne link-underline" data-testid="vacation-call-link">240-372-4804</a>
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-7">
          <div className="border border-warm-border bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.05)] sm:p-10">
            <LeadForm type="vacation" testIdPrefix="vacation-inquiry" />
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default VacationHomes;
